import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    FileText,
    Briefcase,
    Info,
    PhoneCall,
    Plus,
    Trash2,
    Globe,
    RefreshCw,
    Plane,
    MapPin,
    Map,
    Mountain,
    Image as ImageIcon,
    Target,
    Truck,
    Users,
    Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminExchange: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({});
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [activeSection, setActiveSection] = useState<'banner' | 'about' | 'services' | 'branches' | 'clients' | 'contact'>('banner');
    const [activeExchangeBranches, setActiveExchangeBranches] = useState<number[]>([]);
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            setContent(data);
            if (data.Exchange) {
                const ids = [];
                for (let i = 1; i <= 40; i++) {
                    if (data.Exchange[`br_${i}_name`]) {
                        ids.push(i);
                    }
                }
                setActiveExchangeBranches(ids);
            }
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const saveContent = async () => {
        setSaving('Exchange');
        const sectionData = content.Exchange || {};

        // Filter keys to exclude _ar keys as they are sent alongside the main key
        const relevantKeys = Object.keys(sectionData).filter(key => {
            if (key.endsWith('_ar')) return false;
            if (activeSection === 'banner') return key.startsWith('banner_');
            if (activeSection === 'about') return key.startsWith('about_');
            if (activeSection === 'services') return key === 'services_title' || key.startsWith('svc_');
            if (activeSection === 'branches') return key.startsWith('br_');
            if (activeSection === 'clients') return key.startsWith('clients_');
            if (activeSection === 'contact') return key.startsWith('contact_');
            return false;
        });

        try {
            const promises = relevantKeys.map(key => {
                const value = sectionData[key];
                const valueAr = sectionData[`${key}_ar`] || null;
                if (typeof value !== 'string') return Promise.resolve();

                return fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({ 
                        section: 'Exchange', 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: valueAr
                    })
                }).then(res => {
                    if (!res.ok) throw new Error(`Failed to save ${key}`);
                    return res.json();
                });
            });

            await Promise.all(promises);
            console.log('Save successful');
            setSaving(null);
            alert(`${activeSection} updated successfully!`);
        } catch (err) {
            console.error('Save failed:', err);
            alert('Some changes failed to save. Please try again.');
            setSaving(null);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [key]: { file, preview }
        }));
    };

    const saveImages = async () => {
        if (Object.keys(pendingImages).length === 0) return;

        setSaving('images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Exchange');
                formData.append('content_key', key);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    return { key, imageUrl: data.imageUrl };
                }
                return null;
            });

            const results = await Promise.all(promises);

            setContent((prev: any) => ({
                ...prev,
                Exchange: {
                    ...(prev.Exchange || {}),
                    ...results.reduce((acc: any, res) => {
                        if (res) acc[res.key] = res.imageUrl;
                        return acc;
                    }, {})
                }
            }));

            setPendingImages({});
            fetchContent();
        } catch (err) {
            console.error('Batch upload failed');
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const updateField = (key: string, value: string) => {
        setContent((prev: any) => ({
            ...prev,
            Exchange: {
                ...(prev.Exchange || {}),
                [key]: value
            }
        }));
    };

    const NavItem = ({ id, label, icon: Icon }: { id: string, label: string, icon: any }) => (
        <button
            onClick={() => setActiveSection(id as any)}
            className={`flex items-center space-x-3 px-8 py-5 rounded-2xl font-black text-sm tracking-wide transition-all border-2 ${activeSection === id
                ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-200 translate-y-[-4px]'
                : 'bg-white border-slate-200 text-slate-900 hover:border-blue-400 hover:text-blue-600'
                }`}
        >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
        </button>
    );

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Fixed Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <DollarSign className="w-6 h-6 text-blue-600" /> Exchange Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('content')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('images')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>

                        {saving && (
                            <div className="flex items-center space-x-2 text-blue-600 font-bold text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span className="animate-pulse">Syncing...</span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-5xl mx-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-40">
                                <div className="w-16 h-16 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mb-6" />
                                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Fetching Configuration...</p>
                            </div>
                        ) : (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                                {activeTab === 'content' ? (
                                    <>
                                        {/* Section Selector */}
                                        <div className="flex flex-wrap gap-3 pb-4">
                                            {[
                                                { id: 'banner', label: 'Hero Banner', icon: ImageIcon },
                                                { id: 'about', label: 'About Us', icon: Info },
                                                { id: 'services', label: 'Services', icon: Wrench },
                                                { id: 'branches', label: 'Branch Network', icon: Map },
                                                { id: 'clients', label: 'Clients', icon: Users },
                                                { id: 'contact', label: 'Contact Details', icon: PhoneCall }
                                            ].map(sec => (
                                                <button
                                                    key={sec.id}
                                                    onClick={() => setActiveSection(sec.id as any)}
                                                    className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold text-sm transition-all border ${activeSection === sec.id
                                                        ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                                                        : 'bg-white border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                                                >
                                                    <sec.icon className="w-4 h-4" />
                                                    <span>{sec.label}</span>
                                                </button>
                                            ))}
                                        </div>

                                        <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm relative">
                                            <div className="flex items-center justify-between mb-8">
                                                <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                    <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                    {activeSection} Configuration
                                                </h3>
                                                <button
                                                    onClick={saveContent}
                                                    disabled={!!saving}
                                                    className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-emerald-200 active:scale-95"
                                                >
                                                    {saving === 'Exchange' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                    <span>Update {activeSection}</span>
                                                </button>
                                            </div>

                                            {activeSection === 'banner' && (
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Main Title"
                                                        englishValue={content.Exchange?.banner_title || ''}
                                                        arabicValue={content.Exchange?.banner_title_ar}
                                                        onChangeEnglish={val => updateField('banner_title', val)}
                                                        onChangeArabic={val => updateField('banner_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Subtitle Text"
                                                        englishValue={content.Exchange?.banner_subtitle || ''}
                                                        arabicValue={content.Exchange?.banner_subtitle_ar}
                                                        onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                        onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                        rows={3}
                                                    />
                                                </div>
                                            )}

                                            {activeSection === 'about' && (
                                                <div className="space-y-8">
                                                    <div className="space-y-6">
                                                        <BilingualInput 
                                                            label="Intro Title"
                                                            englishValue={content.Exchange?.about_title || ''}
                                                            arabicValue={content.Exchange?.about_title_ar}
                                                            onChangeEnglish={val => updateField('about_title', val)}
                                                            onChangeArabic={val => updateField('about_title_ar', val)}
                                                        />
                                                        <BilingualTextArea 
                                                            label="About Description"
                                                            englishValue={content.Exchange?.about_desc || ''}
                                                            arabicValue={content.Exchange?.about_desc_ar}
                                                            onChangeEnglish={val => updateField('about_desc', val)}
                                                            onChangeArabic={val => updateField('about_desc_ar', val)}
                                                            rows={6}
                                                        />
                                                        <BilingualInput 
                                                            label="Website Info Note"
                                                            englishValue={content.Exchange?.about_web_info || ''}
                                                            arabicValue={content.Exchange?.about_web_info_ar}
                                                            onChangeEnglish={val => updateField('about_web_info', val)}
                                                            onChangeArabic={val => updateField('about_web_info_ar', val)}
                                                            placeholder="e.g. For more information please visit our website:www.hamdanexchange.com"
                                                        />
                                                    </div>

                                                    <div className="border-t border-slate-100 pt-8">
                                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">Service Highlights</h4>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                            {[1, 2, 3].map(i => (
                                                                <div key={i} className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">{i}</span>
                                                                        <select
                                                                            className="text-[10px] font-bold bg-white border border-slate-200 rounded-lg p-1.5 outline-none"
                                                                            value={content.Exchange?.[`about_pt_${i}_icon`] || 'Banknote'}
                                                                            onChange={e => updateField(`about_pt_${i}_icon`, e.target.value)}
                                                                        >
                                                                            <option value="Banknote">Money</option>
                                                                            <option value="Globe2">Globe</option>
                                                                            <option value="Users">Users</option>
                                                                        </select>
                                                                    </div>
                                                                    <BilingualInput 
                                                                        label="Benefit Title"
                                                                        englishValue={content.Exchange?.[`about_pt_${i}_title`] || ''}
                                                                        arabicValue={content.Exchange?.[`about_pt_${i}_title_ar`]}
                                                                        onChangeEnglish={val => updateField(`about_pt_${i}_title`, val)}
                                                                        onChangeArabic={val => updateField(`about_pt_${i}_title_ar`, val)}
                                                                    />
                                                                    <BilingualTextArea 
                                                                        label="Description"
                                                                        englishValue={content.Exchange?.[`about_pt_${i}_desc`] || ''}
                                                                        arabicValue={content.Exchange?.[`about_pt_${i}_desc_ar`]}
                                                                        onChangeEnglish={val => updateField(`about_pt_${i}_desc`, val)}
                                                                        onChangeArabic={val => updateField(`about_pt_${i}_desc_ar`, val)}
                                                                        rows={3}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {activeSection === 'services' && (
                                                <ServiceCardManager
                                                    section="Exchange"
                                                    data={content.Exchange || {}}
                                                    onSaved={fetchContent}
                                                    sectionTitle={content.Exchange?.services_title || ''}
                                                    sectionTitleAr={content.Exchange?.services_title_ar}
                                                    onChangeSectionTitle={val => updateField('services_title', val)}
                                                    onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                                />
                                            )}

                                            {activeSection === 'branches' && (() => {
                                                const E = content.Exchange || {};
                                                const getBranches = () => {
                                                    return activeExchangeBranches.map(i => ({
                                                        i,
                                                        name: E[`br_${i}_name`] || '',
                                                        addr: E[`br_${i}_addr`] || '',
                                                        phone: E[`br_${i}_phone`] || '',
                                                        fax: E[`br_${i}_fax`] || '',
                                                        coord: E[`br_${i}_coord`] || '',
                                                    }));
                                                };

                                                const branches = getBranches();
                                                const nextI = activeExchangeBranches.length > 0 ? Math.max(...activeExchangeBranches) + 1 : 1;

                                                const handleAdd = () => {
                                                    updateField(`br_${nextI}_name`, 'New Branch');
                                                    updateField(`br_${nextI}_addr`, '');
                                                    updateField(`br_${nextI}_phone`, '');
                                                    updateField(`br_${nextI}_fax`, '');
                                                    updateField(`br_${nextI}_coord`, '');
                                                    setActiveExchangeBranches(prev => [...prev, nextI]);
                                                };

                                                const handleDelete = (idx: number) => {
                                                    if (!window.confirm('Delete this branch?')) return;
                                                    ['name', 'addr', 'phone', 'fax', 'coord'].forEach(field => {
                                                        updateField(`br_${idx}_${field}`, '');
                                                    });
                                                    setActiveExchangeBranches(prev => prev.filter(id => id !== idx));
                                                };

                                                return (
                                                    <div className="space-y-4">
                                                        <div className="flex items-center justify-between">
                                                            <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex-1 mr-4">
                                                                <p className="text-xs font-bold text-blue-700">
                                                                    {branches.length} branch(es) — Edit inline, then click "Update branches" to save.
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={handleAdd}
                                                                className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg"
                                                            >
                                                                <span className="text-lg leading-none">+</span> Add Branch
                                                            </button>
                                                        </div>

                                                        {branches.length === 0 && (
                                                            <div className="text-center py-12 text-slate-400">
                                                                <p className="text-sm font-bold">No branches configured yet.</p>
                                                                <p className="text-xs mt-1">Click "Add Branch" to get started.</p>
                                                            </div>
                                                        )}

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                            {branches.map((branch) => (
                                                                <div key={branch.i} className="p-8 rounded-3xl border border-slate-100 bg-slate-50/50 space-y-6">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">BRANCH #{branch.i}</span>
                                                                        <button
                                                                            onClick={() => handleDelete(branch.i)}
                                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-[10px] font-black transition-all"
                                                                        >
                                                                            🗑 Delete
                                                                        </button>
                                                                    </div>
                                                                    <BilingualInput 
                                                                        label="Branch Name"
                                                                        englishValue={E[`br_${branch.i}_name`] || ''}
                                                                        arabicValue={E[`br_${branch.i}_name_ar`]}
                                                                        onChangeEnglish={val => updateField(`br_${branch.i}_name`, val)}
                                                                        onChangeArabic={val => updateField(`br_${branch.i}_name_ar`, val)}
                                                                    />
                                                                    <BilingualTextArea 
                                                                        label="Address / Street"
                                                                        englishValue={E[`br_${branch.i}_addr`] || ''}
                                                                        arabicValue={E[`br_${branch.i}_addr_ar`]}
                                                                        onChangeEnglish={val => updateField(`br_${branch.i}_addr`, val)}
                                                                        onChangeArabic={val => updateField(`br_${branch.i}_addr_ar`, val)}
                                                                        rows={2}
                                                                    />
                                                                    <div className="grid grid-cols-2 gap-6">
                                                                        <BilingualInput 
                                                                            label="Telephone"
                                                                            englishValue={E[`br_${branch.i}_phone`] || ''}
                                                                            arabicValue={E[`br_${branch.i}_phone_ar`]}
                                                                            onChangeEnglish={val => updateField(`br_${branch.i}_phone`, val)}
                                                                            onChangeArabic={val => updateField(`br_${branch.i}_phone_ar`, val)}
                                                                        />
                                                                        <BilingualInput 
                                                                            label="Fax No"
                                                                            englishValue={E[`br_${branch.i}_fax`] || ''}
                                                                            arabicValue={E[`br_${branch.i}_fax_ar`]}
                                                                            onChangeEnglish={val => updateField(`br_${branch.i}_fax`, val)}
                                                                            onChangeArabic={val => updateField(`br_${branch.i}_fax_ar`, val)}
                                                                        />
                                                                    </div>
                                                                    <BilingualInput 
                                                                        label="Coordinates (GPS)"
                                                                        englishValue={E[`br_${branch.i}_coord`] || ''}
                                                                        arabicValue={E[`br_${branch.i}_coord_ar`]}
                                                                        onChangeEnglish={val => updateField(`br_${branch.i}_coord`, val)}
                                                                        onChangeArabic={val => updateField(`br_${branch.i}_coord_ar`, val)}
                                                                        placeholder="e.g. 17.019481,54.061263"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {activeSection === 'clients' && (
                                                <ClientsEditorFields
                                                    data={content.Exchange}
                                                    updateField={updateField}
                                                    defaultDescription="Corporate and individual clients supported through Hamdan Exchange services."
                                                />
                                            )}

                                            {activeSection === 'contact' && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                                    <div className="space-y-6">
                                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b pb-4">HQ Details</h4>
                                                        <div className="space-y-6">
                                                            <BilingualInput 
                                                                label="HQ Name"
                                                                englishValue={content.Exchange?.contact_loc_name || ''}
                                                                arabicValue={content.Exchange?.contact_loc_name_ar}
                                                                onChangeEnglish={val => updateField('contact_loc_name', val)}
                                                                onChangeArabic={val => updateField('contact_loc_name_ar', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="HQ Address"
                                                                englishValue={content.Exchange?.contact_loc_addr || ''}
                                                                arabicValue={content.Exchange?.contact_loc_addr_ar}
                                                                onChangeEnglish={val => updateField('contact_loc_addr', val)}
                                                                onChangeArabic={val => updateField('contact_loc_addr_ar', val)}
                                                                rows={2}
                                                            />
                                                            <div className="grid grid-cols-2 gap-6">
                                                                <BilingualInput 
                                                                    label="Telephone"
                                                                    englishValue={content.Exchange?.contact_loc_phone || ''}
                                                                    arabicValue={content.Exchange?.contact_loc_phone_ar}
                                                                    onChangeEnglish={val => updateField('contact_loc_phone', val)}
                                                                    onChangeArabic={val => updateField('contact_loc_phone_ar', val)}
                                                                />
                                                                <BilingualInput 
                                                                    label="Fax No"
                                                                    englishValue={content.Exchange?.contact_loc_fax || ''}
                                                                    arabicValue={content.Exchange?.contact_loc_fax_ar}
                                                                    onChangeEnglish={val => updateField('contact_loc_fax', val)}
                                                                    onChangeArabic={val => updateField('contact_loc_fax_ar', val)}
                                                                />
                                                            </div>
                                                            <BilingualInput 
                                                                label="HQ Email"
                                                                englishValue={content.Exchange?.contact_loc_email || ''}
                                                                arabicValue={content.Exchange?.contact_loc_email_ar}
                                                                onChangeEnglish={val => updateField('contact_loc_email', val)}
                                                                onChangeArabic={val => updateField('contact_loc_email_ar', val)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="space-y-6">
                                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b pb-4">Muscat Branch</h4>
                                                        <div className="space-y-6">
                                                            <BilingualInput 
                                                                label="Branch Name"
                                                                englishValue={content.Exchange?.contact_muscat_name || ''}
                                                                arabicValue={content.Exchange?.contact_muscat_name_ar}
                                                                onChangeEnglish={val => updateField('contact_muscat_name', val)}
                                                                onChangeArabic={val => updateField('contact_muscat_name_ar', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Telephone"
                                                                englishValue={content.Exchange?.contact_muscat_phone || ''}
                                                                arabicValue={content.Exchange?.contact_muscat_phone_ar}
                                                                onChangeEnglish={val => updateField('contact_muscat_phone', val)}
                                                                onChangeArabic={val => updateField('contact_muscat_phone_ar', val)}
                                                            />
                                                            <BilingualInput 
                                                                label="Fax No"
                                                                englishValue={content.Exchange?.contact_muscat_fax || ''}
                                                                arabicValue={content.Exchange?.contact_muscat_fax_ar}
                                                                onChangeEnglish={val => updateField('contact_muscat_fax', val)}
                                                                onChangeArabic={val => updateField('contact_muscat_fax_ar', val)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <motion.div
                                        key="images-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-12"
                                    >
                                        <div className="flex items-center justify-between border-b pb-6">
                                            <div>
                                                <h3 className="text-2xl font-black text-slate-800 tracking-tight">Exchange Media Library</h3>
                                                <p className="text-slate-500 text-sm font-medium">Manage all visual assets for the Exchange section</p>
                                            </div>
                                            <button
                                                onClick={saveImages}
                                                disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-blue-200 active:scale-95 disabled:bg-slate-300 flex items-center gap-3"
                                            >
                                                {saving === 'images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                                                <span>Push All Changes</span>
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                            {/* Section: Hero Banners */}
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                                        <ImageIcon className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Hero Banner Slides</h4>
                                                        <p className="text-[10px] text-slate-400 font-bold">Recommended: 1920x800px</p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 gap-4">
                                                    {[1, 2, 3].map(i => (
                                                        <div key={i} className="group relative rounded-2xl overflow-hidden border-2 border-slate-100 hover:border-blue-400 transition-all aspect-[21/9] bg-slate-50">
                                                            {(pendingImages[`banner_${i}`] || content.Exchange?.[`banner_${i}`]) ? (
                                                                <img
                                                                    src={pendingImages[`banner_${i}`]?.preview || content.Exchange?.[`banner_${i}`]}
                                                                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                                                    alt={`Slide ${i}`}
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                                    <ImageIcon className="w-8 h-8 mb-2 opacity-20" />
                                                                    <span className="text-[10px] font-bold uppercase tracking-tighter">No Banner {i}</span>
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                                                                <label className="bg-white p-3 rounded-xl shadow-xl cursor-pointer hover:scale-110 transition-transform">
                                                                    <RefreshCw className="w-5 h-5 text-blue-600" />
                                                                    <input type="file" onChange={e => e.target.files?.[0] && handleImageSelect(`banner_${i}`, e.target.files[0])} className="hidden" />
                                                                </label>
                                                                <span className="text-white text-[10px] font-bold uppercase tracking-widest mt-3">Replace Slot {i}</span>
                                                            </div>
                                                            {pendingImages[`banner_${i}`] && (
                                                                <div className="absolute top-4 right-4 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-2">
                                                                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                                                    QUEUED
                                                                </div>
                                                            )}
                                                            <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-md text-white text-[8px] font-black px-2 py-1 rounded-md uppercase">
                                                                Banner {i}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Section: Brand & Identity */}
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                                                        <DollarSign className="w-5 h-5 text-amber-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Brand Configuration</h4>
                                                        <p className="text-[10px] text-slate-400 font-bold">Logo & Identity Assets</p>
                                                    </div>
                                                </div>

                                                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                                    <div className="group relative rounded-2xl overflow-hidden border-2 border-dashed border-slate-200 hover:border-amber-400 transition-all aspect-square max-w-[240px] mx-auto bg-slate-50 flex items-center justify-center">
                                                        {(pendingImages['about_image'] || content.Exchange?.[`about_image`]) ? (
                                                            <img
                                                                src={pendingImages['about_image']?.preview || content.Exchange?.[`about_image`]}
                                                                className="w-full h-full object-contain p-6"
                                                                alt="Exchange Logo"
                                                            />
                                                        ) : (
                                                            <div className="text-center p-6">
                                                                <Plus className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                                                <span className="text-[10px] font-bold text-slate-400 uppercase">Upload Logo</span>
                                                            </div>
                                                        )}
                                                        <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                                                            <label className="bg-white p-4 rounded-2xl shadow-2xl cursor-pointer hover:scale-110 transition-transform">
                                                                <Plus className="w-6 h-6 text-amber-600" />
                                                                <input type="file" onChange={e => e.target.files?.[0] && handleImageSelect('about_image', e.target.files[0])} className="hidden" />
                                                            </label>
                                                            <span className="text-white text-[10px] font-bold uppercase tracking-widest mt-4">Brand Image</span>
                                                        </div>
                                                        {pendingImages['about_image'] && (
                                                            <div className="absolute top-4 right-4 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg">
                                                                NEW
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-8 pt-6 border-t border-slate-50">
                                                        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                            <span>Deployment Status</span>
                                                            <span className="text-emerald-500 flex items-center gap-1">
                                                                <CheckCircle2 className="w-3 h-3" /> Live
                                                            </span>
                                                        </div>
                                                        <p className="mt-2 text-xs text-slate-500 font-medium leading-relaxed">
                                                            This image appears in the 'About Us' section of the Hamdan Exchange page. Transparent PNG recommended.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Section: Service Images */}
                                            <div className="space-y-6 lg:col-span-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                                        <Wrench className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Service Images</h4>
                                                        <p className="text-[10px] text-slate-400 font-bold">Upload an image for each of the 3 services</p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    {[1, 2, 3].map(i => (
                                                        <div key={i} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                                                            <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest block text-center">Service {i} Image</span>
                                                            <div className="group relative rounded-2xl overflow-hidden border-2 border-slate-100 hover:border-blue-400 transition-all aspect-video bg-slate-50 flex items-center justify-center">
                                                                {(pendingImages[`svc_${i}_image`] || content.Exchange?.[`svc_${i}_image`]) ? (
                                                                    <img
                                                                        src={pendingImages[`svc_${i}_image`]?.preview || content.Exchange?.[`svc_${i}_image`]}
                                                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                                                        alt={`Service ${i}`}
                                                                    />
                                                                ) : (
                                                                    <div className="text-center p-6 text-slate-300">
                                                                        <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                                                        <span className="text-[10px] font-bold uppercase">No Image</span>
                                                                    </div>
                                                                )}
                                                                <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                                                                    <label className="bg-white p-3 rounded-xl shadow-xl cursor-pointer hover:scale-110 transition-transform">
                                                                        <RefreshCw className="w-5 h-5 text-blue-600" />
                                                                        <input type="file" onChange={e => e.target.files?.[0] && handleImageSelect(`svc_${i}_image`, e.target.files[0])} className="hidden" />
                                                                    </label>
                                                                </div>
                                                                {pendingImages[`svc_${i}_image`] && (
                                                                    <div className="absolute top-2 right-2 bg-emerald-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-lg animate-pulse">
                                                                        QUEUED
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                                <ClientLogoUploadGrid
                                                    data={content.Exchange}
                                                    pendingImages={pendingImages}
                                                    onSelect={handleImageSelect}
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                <div className="h-20" />
                            </motion.div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminExchange;
